import { z } from 'zod';
import { Currency, IsoDatetime } from './primitives.js';

const Id = z.string().max(40);

// Trust ladder (01 §6, how-it-works §6): 0 informative · 1 reversible-cheap
// (auto + silent report) · 2 reversible-expensive (approval with options) ·
// 3 significant money / commitment / third party (always approval) ·
// 4 never autonomous. One ladder, four applications (decision 4).
export const TrustLevel = z.number().int().min(0).max(4);
export type TrustLevel = z.infer<typeof TrustLevel>;

// Standing permission (S53): "order X up to Y AED = on your own".
export const StandingPermission = z.object({
  max_minor: z.number().int().min(0),
  note: z.string().optional(),
});
export type StandingPermission = z.infer<typeof StandingPermission>;

// Demotion on a corrected autonomous action: no tenure — after a demotion
// the streak counter starts from zero.
export const TrustDemotion = z.object({
  from_level: TrustLevel,
  reason: z.string().min(1),
  at: IsoDatetime,
});
export type TrustDemotion = z.infer<typeof TrustDemotion>;

// Trust is earned, never granted (decision 4). One profile per subject
// (human staff OR AI agent — the same ladder serves both, S33) per domain.
export const TrustProfile = z
  .object({
    id: Id,
    household_id: Id,
    staff_id: Id.nullable(),
    // NULL unless the subject is an agent (e.g. 'agent:famanage-core').
    agent_key: z.string().max(80).nullable(),
    // Domain: 'restaurant_booking' | 'grocery_order' | 'pet_care' | ...
    domain: z.string().min(1).max(80),
    level: TrustLevel.default(0),
    // Conservative promotion mechanism: consecutive approvals without a
    // correction.
    consecutive_approvals: z.number().int().min(0).default(0),
    corrections_total: z.number().int().min(0).default(0),
    // The system SUGGESTS a promotion after N approvals; the owner decides.
    promotion_suggested: z.boolean().default(false),
    suggested_level: TrustLevel.nullable(),
    standing_permission: StandingPermission.nullable(),
    // Per-profile privacy rules (S61); NULL = conservative household default.
    privacy_rules: z.record(z.any()).nullable(),
    last_demotion: TrustDemotion.nullable(),
    created_at: IsoDatetime,
    updated_at: IsoDatetime,
  })
  .refine(
    (profile) => (profile.staff_id === null) !== (profile.agent_key === null),
    'a trust profile belongs to exactly one staff member or agent',
  );
export type TrustProfile = z.infer<typeof TrustProfile>;

// Every approval/correction/autonomous action is a learning signal (01 §6.2,
// 02 §6.1) — the trust ladder IS the calibration. Append-only.
export const AutonomySignal = z.enum([
  'approved_as_is',
  'approved_with_correction',
  'rejected',
  'expired',
  'auto_executed_reported',
  'auto_corrected',
  'blocked_by_safety',
  'promotion_suggested',
  'promotion_accepted',
  'promotion_declined',
  'demoted',
]);
export type AutonomySignal = z.infer<typeof AutonomySignal>;

export const AutonomyEvent = z.object({
  id: Id,
  household_id: Id,
  trust_profile_id: Id.nullable(),
  situation_id: Id.nullable(),
  // Stable trust key: 'restaurant_booking', 'valet_parking', ...
  action_type: z.string().max(80).nullable(),
  // The card's round within the situation (a rejection opens a new round).
  round: z.number().int().min(1).nullable(),
  signal: AutonomySignal,
  level_at_time: TrustLevel,
  // What the system proposed or did, as shown to the owner.
  proposed_action: z.record(z.any()).nullable(),
  // An owner correction is the most valuable feedback: what changed and why.
  correction_detail: z.record(z.any()).nullable(),
  actor_user_id: Id.nullable(),
  created_at: IsoDatetime,
});
export type AutonomyEvent = z.infer<typeof AutonomyEvent>;

// The money ladder as config, not code (01 §7): changing an amount is a row
// update, not a deploy. Defaults: ≤200 AED auto_report · 200–1,000 one_owner
// · 1,000+ both_owners · a loan always needs approval + a ledger entry.
export const MoneyActionKind = z.enum([
  'purchase',
  'loan',
  'petty_cash',
  'salary_advance',
  'vendor_payment',
]);
export type MoneyActionKind = z.infer<typeof MoneyActionKind>;

export const MoneyApproval = z.enum(['auto_report', 'one_owner', 'both_owners', 'forbidden']);
export type MoneyApproval = z.infer<typeof MoneyApproval>;

export const MoneyPermissionRule = z
  .object({
    id: Id,
    household_id: Id,
    action_kind: MoneyActionKind,
    // Optional domain for precision ('pet_vet', 'groceries'); NULL = general.
    domain: z.string().max(80).nullable(),
    min_minor: z.number().int().min(0).default(0),
    // NULL = no cap.
    max_minor: z.number().int().min(0).nullable(),
    // Household currency — a field, not a constant.
    currency: Currency,
    approval: MoneyApproval,
    // Turns an approval into a mandatory ledger entry (loans always, S14).
    requires_ledger: z.boolean().default(false),
    note: z.string().nullable(),
    updated_by_user_id: Id.nullable(),
    created_at: IsoDatetime,
    updated_at: IsoDatetime,
  })
  .refine(
    (rule) => rule.max_minor === null || rule.max_minor >= rule.min_minor,
    'max_minor must be greater than or equal to min_minor',
  );
export type MoneyPermissionRule = z.infer<typeof MoneyPermissionRule>;
